import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  CheckCircle2,
  Globe,
  Key,
  Lock,
  RefreshCw,
  Shield,
  UserPlus,
  Users,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassInput, GlassModal, GlassSelect, GlassTextarea, FadeIn } from '../../components/ui';
import { api } from '../../hooks/useApi';

const tabs = [
  { id: 'usuarios', label: 'Usuários', icon: Users },
  { id: 'perfis', label: 'Perfis', icon: Shield },
  { id: 'sessoes', label: 'Sessões', icon: Lock },
  { id: 'auditoria', label: 'Auditoria', icon: Activity },
  { id: 'seguranca', label: 'Segurança', icon: Key },
  { id: 'dominio', label: 'Domínio', icon: Globe },
];

const INITIAL_USER_FORM = {
  email: '',
  username: '',
  first_name: '',
  last_name: '',
  phone: '',
  role_id: '',
  is_active: true,
};

const INITIAL_PASSWORD_FORM = {
  user_id: '',
  name: '',
  password: '',
};

const INITIAL_ROLE_FORM = {
  name: '',
  description: '',
  permission_ids: [],
};

const MODULE_LABELS = {
  dashboard: 'Painel geral',
  financeiro: 'Financeiro',
  contabilidade: 'Financeiro e contabilidade',
  construtora: 'Construtora',
  imobiliaria: 'Imobiliária',
  marketing: 'Marketing',
  rh: 'RH',
  frota: 'Frota',
  estoque: 'Estoque e equipamentos',
  juridico: 'Jurídico',
  documentos: 'Documentos/OCR',
  revisoes: 'Revisões e aprovações',
  users: 'Usuários',
  roles: 'Perfis de acesso',
  audit: 'Auditoria',
  notifications: 'Notificações',
  predictive: 'IA preditiva',
  nexus_team: 'Nexus Team',
  geral: 'Geral',
};

const ACTION_LABELS = {
  read: 'Pode ver',
  write: 'Pode cadastrar e alterar',
  create: 'Pode criar',
  update: 'Pode editar',
  delete: 'Pode excluir',
  admin: 'Pode administrar',
};

const ROLE_EXPLAINERS = {
  admin: 'Dono da chave mestra: vê, altera e configura tudo no ERP.',
  gestor: 'Acompanha a operação e decide, mas sem mexer em configurações críticas.',
  financeiro: 'Cuida de contas, caixa, cobranças, cartões e visão financeira.',
  construtora: 'Cuida de obras, clientes, unidades, terrenos, contratos e documentos da construção.',
  juridico: 'Cuida de processos, cobranças, prazos, documentos e escritório jurídico.',
  rh: 'Cuida de funcionários, ponto, documentos, férias e empreiteiras.',
  frota: 'Cuida de veículos, multas, manutenções, pneus, documentos e uso da frota.',
  estoque: 'Cuida de materiais, equipamentos e movimentações de estoque.',
  comercial: 'Acompanha imobiliária, captação, marketing e relacionamento comercial.',
  consulta_operacional: 'Somente consulta: vê informações liberadas, sem alterar cadastros.',
};

const readableModule = (module) => MODULE_LABELS[module] || module;
const readableAuditAction = (action) => String(action || '-').replace(/_/g, ' ');

const readablePermission = (permission) => {
  const [module, action] = String(permission.name || '').split(':');
  return `${readableModule(module)}: ${ACTION_LABELS[action] || permission.description || action || 'acesso'}`;
};

const explainRole = (role) => ROLE_EXPLAINERS[role.name] || role.description || 'Perfil personalizado com permissões escolhidas por área.';

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
};

const groupPermissions = (permissions) =>
  permissions.reduce((acc, permission) => {
    const module = permission.module || 'geral';
    acc[module] = acc[module] || [];
    acc[module].push(permission);
    return acc;
  }, {});

export const Settings = () => {
  const [activeTab, setActiveTab] = useState('usuarios');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilters, setAuditFilters] = useState({ module: '', actor: '', status: '' });
  const [mfa, setMfa] = useState({ enabled: false, recovery_codes_remaining: 0 });
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRecoveryCodes, setMfaRecoveryCodes] = useState([]);
  const [mfaDisable, setMfaDisable] = useState({ current_password: '', code: '' });
  const [summary, setSummary] = useState(null);
  const [modal, setModal] = useState('');
  const [userForm, setUserForm] = useState(INITIAL_USER_FORM);
  const [passwordForm, setPasswordForm] = useState(INITIAL_PASSWORD_FORM);
  const [roleForm, setRoleForm] = useState(INITIAL_ROLE_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, rolesRes, permissionsRes, sessionsRes, auditRes, summaryRes, mfaRes] = await Promise.all([
        api.get('/auth/users', { params: { per_page: 200 } }),
        api.get('/auth/roles'),
        api.get('/auth/permissions'),
        api.get('/auth/sessions'),
        api.get('/audit/events', { params: { per_page: 100, ...Object.fromEntries(Object.entries(auditFilters).filter(([, value]) => value)) } }),
        api.get('/auth/admin/access-summary'),
        api.get('/auth/mfa/status'),
      ]);
      setUsers(usersRes.data.items || []);
      setRoles(rolesRes.data || []);
      setPermissions(permissionsRes.data || []);
      setSessions(sessionsRes.data.sessions || []);
      setAuditEvents(auditRes.data.items || []);
      setAuditTotal(auditRes.data.total || 0);
      setSummary(summaryRes.data || null);
      setMfa(mfaRes.data || { enabled: false, recovery_codes_remaining: 0 });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao carregar acessos.');
    } finally {
      setLoading(false);
    }
  }, [auditFilters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const roleOptions = useMemo(
    () => roles.map((role) => ({ value: role.id, label: `${role.name}${role.is_system ? ' · sistema' : ''} — ${explainRole(role)}` })),
    [roles]
  );
  const permissionsByModule = useMemo(() => groupPermissions(permissions), [permissions]);

  const closeModal = () => {
    setModal('');
    setError('');
    setUserForm(INITIAL_USER_FORM);
    setPasswordForm(INITIAL_PASSWORD_FORM);
    setRoleForm(INITIAL_ROLE_FORM);
  };

  const openUserModal = (user = null) => {
    setUserForm(user ? {
      ...INITIAL_USER_FORM,
      ...user,
      role_id: user.role_id || roles.find((role) => role.name === user.role)?.id || '',
    } : INITIAL_USER_FORM);
    setModal('usuario');
  };

  const saveUser = async () => {
    if (!userForm.email || !userForm.username) {
      setError('Email e usuário são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      if (userForm.id) {
        await api.put(`/auth/users/${userForm.id}`, {
          email: userForm.email,
          first_name: userForm.first_name,
          last_name: userForm.last_name,
          phone: userForm.phone,
          role_id: userForm.role_id || null,
        });
        await api.patch(`/auth/users/${userForm.id}/status`, { is_active: userForm.is_active });
      } else {
        await api.post('/auth/users', userForm);
      }
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao salvar usuário.');
    } finally {
      setSaving(false);
    }
  };

  const openPasswordModal = (user) => {
    setPasswordForm({
      user_id: user.id,
      name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username,
      password: '',
    });
    setModal('senha');
  };

  const savePassword = async () => {
    if (!passwordForm.password || passwordForm.password.length < 8) {
      setError('A nova senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/auth/users/${passwordForm.user_id}/password`, { password: passwordForm.password });
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao alterar senha.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (user) => {
    setSaving(true);
    try {
      await api.patch(`/auth/users/${user.id}/status`, { is_active: !user.is_active });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao alterar status.');
    } finally {
      setSaving(false);
    }
  };

  const openRoleModal = (role = null) => {
    setRoleForm(role ? {
      ...role,
      permission_ids: permissions.filter((permission) => (role.permissions || []).includes(permission.name)).map((permission) => permission.id),
    } : INITIAL_ROLE_FORM);
    setModal('perfil');
  };

  const togglePermission = (permissionId) => {
    setRoleForm((current) => {
      const exists = current.permission_ids.includes(permissionId);
      return {
        ...current,
        permission_ids: exists
          ? current.permission_ids.filter((id) => id !== permissionId)
          : [...current.permission_ids, permissionId],
      };
    });
  };

  const saveRole = async () => {
    if (!roleForm.name) {
      setError('Nome do perfil é obrigatório.');
      return;
    }
    setSaving(true);
    try {
      if (roleForm.id) {
        await api.put(`/auth/roles/${roleForm.id}`, roleForm);
      } else {
        await api.post('/auth/roles', roleForm);
      }
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao salvar perfil.');
    } finally {
      setSaving(false);
    }
  };

  const applyDefaultRoles = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post('/auth/admin/default-roles');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao aplicar perfis padrão.');
    } finally {
      setSaving(false);
    }
  };

  const syncAccessPolicy = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post('/auth/admin/sync-access');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao sincronizar acessos.');
    } finally {
      setSaving(false);
    }
  };

  const revokeSession = async (sessionId) => {
    setSaving(true);
    try {
      await api.del(`/auth/sessions/${sessionId}`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao encerrar sessão.');
    } finally {
      setSaving(false);
    }
  };

  const startMfaSetup = async () => {
    setSaving(true); setError('');
    try {
      const response = await api.post('/auth/mfa/setup');
      setMfaSetup(response.data); setMfaCode(''); setMfaRecoveryCodes([]);
    } catch (err) { setError(err.response?.data?.error || 'Não foi possível iniciar a configuração do MFA.'); }
    finally { setSaving(false); }
  };

  const confirmMfaSetup = async () => {
    setSaving(true); setError('');
    try {
      const response = await api.post('/auth/mfa/confirm', { secret: mfaSetup.secret, code: mfaCode });
      setMfa(response.data); setMfaRecoveryCodes(response.data.recovery_codes || []); setMfaSetup(null); setMfaCode('');
    } catch (err) { setError(err.response?.data?.error || 'Não foi possível confirmar o MFA.'); }
    finally { setSaving(false); }
  };

  const disableMfa = async () => {
    setSaving(true); setError('');
    try {
      const response = await api.post('/auth/mfa/disable', mfaDisable);
      setMfa(response.data); setMfaDisable({ current_password: '', code: '' });
    } catch (err) { setError(err.response?.data?.error || 'Não foi possível desativar o MFA.'); }
    finally { setSaving(false); }
  };

  const renderUsers = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <GlassCard className="p-5" hover={false}>
          <p className="text-sm text-white/50">Usuários</p>
          <p className="mt-2 text-2xl font-semibold text-white/90">{summary?.users_total || 0}</p>
        </GlassCard>
        <GlassCard className="p-5" hover={false}>
          <p className="text-sm text-white/50">Ativos</p>
          <p className="mt-2 text-2xl font-semibold text-white/90">{summary?.users_active || 0}</p>
        </GlassCard>
        <GlassCard className="p-5" hover={false}>
          <p className="text-sm text-white/50">Sem perfil</p>
          <p className="mt-2 text-2xl font-semibold text-white/90">{summary?.users_without_role || 0}</p>
        </GlassCard>
        <GlassCard className="p-5" hover={false}>
          <p className="text-sm text-white/50">Sessões ativas</p>
          <p className="mt-2 text-2xl font-semibold text-white/90">{summary?.active_sessions || 0}</p>
        </GlassCard>
      </div>
      <GlassCard>
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 p-6">
          <div>
            <h3 className="text-lg font-semibold text-white/90">Usuários do ERP</h3>
            <p className="text-sm text-white/50">Acesso de pessoas reais ao sistema</p>
          </div>
          <GlassButton icon={UserPlus} onClick={() => openUserModal()}>Novo usuário</GlassButton>
        </div>
        <div className="divide-y divide-white/10">
          {users.map((user) => (
            <div key={user.id} className="grid grid-cols-2 gap-3 p-4 text-sm md:grid-cols-7">
              <div className="md:col-span-2">
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-white/35 md:hidden">Pessoa</p>
                <p className="font-medium text-white/90">{`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username}</p>
                <p className="text-white/45">{user.email}</p>
              </div>
              <div><p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-white/35 md:hidden">Usuário</p><span className="text-white/60">{user.username}</span></div>
              <div><p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-white/35 md:hidden">Perfil</p><span className="text-white/60">{user.role || 'sem perfil'}</span></div>
              <div><p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-white/35 md:hidden">Situação</p><span className={user.is_active ? 'text-green-300' : 'text-red-300'}>{user.is_active ? 'ativo' : 'inativo'}</span></div>
              <div><p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-white/35 md:hidden">Último acesso</p><span className="text-white/45">{formatDateTime(user.last_login_at)}</span></div>
              <div className="col-span-2 flex flex-wrap gap-3 md:col-span-1">
                <button className="text-blue-300 hover:text-blue-200" onClick={() => openUserModal(user)}>Editar</button>
                <button className="text-amber-300 hover:text-amber-200" onClick={() => openPasswordModal(user)}>Senha</button>
                <button className="text-white/60 hover:text-white" onClick={() => toggleStatus(user)}>{user.is_active ? 'Desativar' : 'Ativar'}</button>
              </div>
            </div>
          ))}
          {!users.length && <p className="p-6 text-sm text-white/50">Nenhum usuário encontrado.</p>}
        </div>
      </GlassCard>
    </div>
  );

  const renderRoles = () => (
    <div className="space-y-5">
      <GlassCard className="p-5" hover={false}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white/90">Perfis padrão por área</h3>
            <p className="text-sm text-white/50">Escolha um perfil pronto ou crie um perfil combinado, como Financeiro + Construtora.</p>
          </div>
          <GlassButton variant="secondary" icon={RefreshCw} onClick={applyDefaultRoles} loading={saving}>
            Aplicar perfis padrão
          </GlassButton>
        </div>
      </GlassCard>
      <div className="flex justify-end">
        <GlassButton icon={Shield} onClick={() => openRoleModal()}>Novo perfil</GlassButton>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {roles.map((role) => (
          <GlassCard key={role.id} className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-white/90">{role.name}</h3>
                <p className="mt-1 text-sm text-white/50">{explainRole(role)}</p>
              </div>
              <GlassButton variant="secondary" size="sm" onClick={() => openRoleModal(role)}>Editar</GlassButton>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {(role.permissions || []).slice(0, 12).map((permission) => (
                <span key={permission} className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-1 text-xs text-white/60">
                  {readablePermission({ name: permission })}
                </span>
              ))}
              {(role.permissions || []).length > 12 && <span className="text-xs text-white/40">+{role.permissions.length - 12}</span>}
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );

  const renderSessions = () => (
    <GlassCard>
      <div className="border-b border-white/10 p-6">
        <h3 className="text-lg font-semibold text-white/90">Minhas sessões</h3>
        <p className="text-sm text-white/50">Dispositivos e logins vinculados ao usuário atual</p>
      </div>
      <div className="divide-y divide-white/10">
        {sessions.map((session) => (
          <div key={session.id} className="grid grid-cols-1 gap-3 p-4 text-sm md:grid-cols-5">
            <div><p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-white/35 md:hidden">Endereço</p><span className="text-white/90">{session.ip_address || '-'}</span></div>
            <div className="md:col-span-2"><p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-white/35 md:hidden">Dispositivo</p><span className="break-words text-white/60">{session.user_agent || '-'}</span></div>
            <div><p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-white/35 md:hidden">Situação</p><span className={session.is_active ? 'text-green-300' : 'text-white/40'}>{session.is_active ? 'ativa' : 'encerrada'}</span></div>
            <button className="w-fit text-red-300 hover:text-red-200 disabled:text-white/30" disabled={!session.is_active} onClick={() => revokeSession(session.id)}>Encerrar</button>
          </div>
        ))}
        {!sessions.length && <p className="p-6 text-sm text-white/50">Nenhuma sessão registrada.</p>}
      </div>
    </GlassCard>
  );

  const renderAudit = () => (
    <GlassCard>
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 p-6">
        <div>
          <h3 className="text-lg font-semibold text-white/90">Auditoria operacional</h3>
          <p className="text-sm text-white/50">Eventos de criação, alteração e confirmação. Dados sensíveis do formulário não são gravados aqui.</p>
        </div>
        <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-sm text-white/60">
          {auditTotal} evento(s)
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 border-b border-white/10 p-4 md:grid-cols-4">
        <select value={auditFilters.module} onChange={(event) => setAuditFilters((current) => ({ ...current, module: event.target.value }))} className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white">
          <option value="">Todas as áreas</option>
          {Object.entries(MODULE_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        <input value={auditFilters.actor} onChange={(event) => setAuditFilters((current) => ({ ...current, actor: event.target.value }))} placeholder="Filtrar por usuário" className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/35" />
        <select value={auditFilters.status} onChange={(event) => setAuditFilters((current) => ({ ...current, status: event.target.value }))} className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white">
          <option value="">Todos os resultados</option><option value="success">Concluídos</option><option value="error">Com erro</option>
        </select>
        <button onClick={() => setAuditFilters({ module: '', actor: '', status: '' })} className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/70 hover:bg-white/10">Limpar filtros</button>
      </div>
      <div className="divide-y divide-white/10">
        {auditEvents.map((event) => (
          <div key={event.id} className="grid grid-cols-1 gap-3 p-4 text-sm lg:grid-cols-7">
            <div className="lg:col-span-2">
              <p className="font-medium text-white/90">{readableAuditAction(event.action)}</p>
              <p className="text-white/45">{formatDateTime(event.created_at)}</p>
            </div>
            <span className="text-white/60">{readableModule(event.module)}</span>
            <span className="text-white/60">{event.entity_label || event.entity_type || event.entity_id || '-'}</span>
            <span className="text-white/50">{event.actor_email || event.actor_username || 'sistema'}</span>
            <span className="text-white/45">{event.ip_address || '-'}</span>
            <span className={event.status === 'success' ? 'text-green-300' : 'text-amber-300'}>{event.status}</span>
          </div>
        ))}
        {!auditEvents.length && <p className="p-6 text-sm text-white/50">Nenhum evento de auditoria registrado.</p>}
      </div>
    </GlassCard>
  );

  const renderSecurity = () => (
    <div className="space-y-5">
      <GlassCard className="p-6">
        <h3 className="text-lg font-semibold text-white/90">Autenticação em dois fatores</h3>
        <p className="mt-1 text-sm text-white/50">Protege esta conta com aplicativo autenticador. A ativação é opcional e só passa a valer depois da confirmação.</p>
        <div className="mt-5 flex flex-wrap items-center gap-3"><span className={`rounded-full px-3 py-1 text-sm ${mfa.enabled ? 'bg-emerald-500/15 text-emerald-200' : 'bg-amber-500/15 text-amber-100'}`}>{mfa.enabled ? 'MFA ativo' : 'MFA não ativado'}</span>{mfa.enabled && <span className="text-sm text-white/50">{mfa.recovery_codes_remaining} código(s) de recuperação disponível(is)</span>}</div>
        {!mfa.enabled && !mfaSetup && <GlassButton className="mt-5" icon={Shield} onClick={startMfaSetup} loading={saving}>Configurar MFA</GlassButton>}
      </GlassCard>
      {mfaSetup && <GlassCard className="p-4 sm:p-6"><h3 className="font-semibold text-white/90">1. Adicione ao autenticador</h3><p className="mt-1 text-sm text-white/50">Escaneie o QR code no Google Authenticator, Microsoft Authenticator ou app equivalente.</p><img src={mfaSetup.qr_code_data_url} alt="QR code do MFA" className="mt-4 h-auto w-full max-w-[12rem] rounded-lg bg-white p-2" /><p className="mt-4 break-words text-xs text-white/45">Se não puder escanear, use esta chave manual: <span className="select-all font-mono text-white/80">{mfaSetup.secret}</span></p><label className="mt-5 block text-sm text-white/70">2. Código exibido pelo aplicativo<input value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} autoComplete="one-time-code" className="mt-2 w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-white" /></label><div className="mt-5 flex flex-col gap-3 sm:flex-row"><GlassButton onClick={confirmMfaSetup} loading={saving}>Confirmar e ativar</GlassButton><GlassButton variant="ghost" onClick={() => { setMfaSetup(null); setMfaCode(''); }}>Cancelar</GlassButton></div></GlassCard>}
      {mfaRecoveryCodes.length > 0 && <GlassCard className="border border-amber-400/25 bg-amber-500/10 p-6"><h3 className="font-semibold text-amber-100">Guarde estes códigos agora</h3><p className="mt-1 text-sm text-amber-100/75">Cada código funciona uma única vez. Eles não serão exibidos novamente.</p><div className="mt-4 grid grid-cols-2 gap-2 font-mono text-sm text-white">{mfaRecoveryCodes.map((code) => <span key={code} className="rounded bg-black/20 px-3 py-2">{code}</span>)}</div><GlassButton variant="secondary" className="mt-4" onClick={() => setMfaRecoveryCodes([])}>Entendi, já guardei</GlassButton></GlassCard>}
      {mfa.enabled && <GlassCard className="p-6"><h3 className="font-semibold text-white/90">Desativar MFA</h3><p className="mt-1 text-sm text-white/50">Exige a senha atual e um código do autenticador ou de recuperação.</p><div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2"><GlassInput label="Senha atual" type="password" value={mfaDisable.current_password} onChange={(event) => setMfaDisable({ ...mfaDisable, current_password: event.target.value })} /><GlassInput label="Código de autenticação" value={mfaDisable.code} onChange={(event) => setMfaDisable({ ...mfaDisable, code: event.target.value })} /></div><GlassButton variant="danger" className="mt-4" onClick={disableMfa} loading={saving}>Desativar MFA</GlassButton></GlassCard>}
    </div>
  );

  const renderDomain = () => (
    <div className="space-y-5">
      <GlassCard className="p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm text-white/50">Domínio recomendado</p>
            <h3 className="mt-2 text-xl font-semibold text-white/90">{summary?.domain_recommended || 'erp.manicamarinconstrutora.com.br'}</h3>
          </div>
          <span className="w-fit rounded-full border border-amber-400/30 bg-amber-500/15 px-3 py-2 text-sm text-amber-100">
            {summary?.external_access_status || 'planejado'}
          </span>
        </div>
      </GlassCard>
      <GlassCard>
        <div className="border-b border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white/90">Checklist técnico</h3>
        </div>
        <div className="divide-y divide-white/10">
          {(summary?.domain_steps || []).map((step, index) => (
            <div key={step} className="flex items-start gap-3 p-4 text-sm">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-white/30" />
              <div>
                <p className="text-white/80">{step}</p>
                <p className="text-xs text-white/40">Etapa {index + 1}</p>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );

  const renderContent = () => {
    if (activeTab === 'usuarios') return renderUsers();
    if (activeTab === 'perfis') return renderRoles();
    if (activeTab === 'sessoes') return renderSessions();
    if (activeTab === 'auditoria') return renderAudit();
    if (activeTab === 'seguranca') return renderSecurity();
    if (activeTab === 'dominio') return renderDomain();
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Administração</h1>
          <p className="text-white/50">Usuários, perfis, sessões e acesso externo do ERP</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <GlassButton className="flex-1 sm:flex-none" variant="secondary" icon={RefreshCw} onClick={syncAccessPolicy} loading={saving}>Sincronizar acessos</GlassButton>
          <GlassButton className="flex-1 sm:flex-none" variant="secondary" icon={RefreshCw} onClick={loadData} loading={loading}>Atualizar</GlassButton>
        </div>
      </div>

      {error && (
        <GlassCard className="border-red-400/30 bg-red-500/10 p-4" hover={false}>
          <p className="text-sm text-red-100">{error}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 gap-4 lg:gap-6 lg:grid-cols-4">
        <GlassCard className="h-fit p-2 sm:p-4">
          <nav className="flex gap-1 overflow-x-auto pb-1 lg:block lg:space-y-1 lg:overflow-visible">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-colors lg:w-full lg:gap-3 lg:px-4 lg:py-3 ${
                    activeTab === tab.id
                      ? 'border border-white/10 bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 text-white'
                      : 'text-white/60 hover:bg-white/5'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </GlassCard>

        <div className="lg:col-span-3">
          <FadeIn>{renderContent()}</FadeIn>
        </div>
      </div>

      <GlassModal
        isOpen={modal === 'usuario'}
        onClose={closeModal}
        title={userForm.id ? 'Editar usuário' : 'Novo usuário'}
        icon={Users}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={saveUser} loading={saving}>Salvar usuário</GlassButton></>}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <GlassInput label="Nome" value={userForm.first_name || ''} onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })} />
          <GlassInput label="Sobrenome" value={userForm.last_name || ''} onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })} />
          <GlassInput label="Email" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
          <GlassInput label="Usuário" value={userForm.username} disabled={Boolean(userForm.id)} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} />
          <GlassInput label="Telefone" value={userForm.phone || ''} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} />
          {!userForm.id && <p className="rounded-lg border border-cyan-300/15 bg-cyan-400/5 px-3 py-2 text-sm text-cyan-100/85 md:col-span-2">Ao salvar, o ERP enviará para este e-mail um link único para criar a senha de acesso.</p>}
          <GlassSelect label="Perfil" value={userForm.role_id || ''} onChange={(e) => setUserForm({ ...userForm, role_id: e.target.value })} options={[{ value: '', label: 'Sem perfil' }, ...roleOptions]} />
          <GlassSelect label="Status" value={userForm.is_active ? 'ativo' : 'inativo'} onChange={(e) => setUserForm({ ...userForm, is_active: e.target.value === 'ativo' })} options={[{ value: 'ativo', label: 'Ativo' }, { value: 'inativo', label: 'Inativo' }]} />
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'senha'}
        onClose={closeModal}
        title="Alterar senha"
        icon={Key}
        size="md"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={savePassword} loading={saving}>Salvar senha</GlassButton></>}
      >
        <div className="space-y-4">
          <p className="text-sm text-white/60">{passwordForm.name}</p>
          <GlassInput label="Nova senha" type="password" value={passwordForm.password} onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })} />
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'perfil'}
        onClose={closeModal}
        title={roleForm.id ? 'Editar perfil' : 'Novo perfil'}
        icon={Shield}
        size="xl"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={saveRole} loading={saving}>Salvar perfil</GlassButton></>}
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <GlassInput label="Nome" value={roleForm.name} disabled={Boolean(roleForm.is_system)} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} />
            <GlassTextarea label="Descrição" value={roleForm.description || ''} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {Object.entries(permissionsByModule).map(([module, items]) => (
              <GlassCard key={module} className="p-4" hover={false}>
                <h4 className="font-semibold text-white/90">{readableModule(module)}</h4>
                <p className="mt-1 text-xs text-white/45">Marque o que este perfil pode fazer nesta área.</p>
                <div className="mt-3 space-y-2">
                  {items.map((permission) => (
                    <label key={permission.id} className="flex items-start gap-3 rounded-lg bg-white/[0.03] p-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={roleForm.permission_ids.includes(permission.id)}
                        onChange={() => togglePermission(permission.id)}
                      />
                      <span>
                        <span className="block text-sm text-white/85">{readablePermission(permission)}</span>
                        <span className="block text-xs text-white/45">{permission.description || permission.action}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </GlassModal>
    </div>
  );
};

export default Settings;
